import { useEffect, useMemo, useState } from 'react';
import { notifySupheroChatReady } from './supportChannels';

const SUPHERO_SCRIPT_SRC = 'https://suphero.io/apps/telegramchat/php/assets/chat_widget.js?v=socket26';
const SUPHERO_API_BASE = 'https://suphero.io/apps/telegramchat/php';
const USER_DATA_ENDPOINT = 'https://tivigo.store/endpoint.php?endpoint=user_data';
const SUPHERO_STORAGE_PREFIX = 'tivigo.telegramchat.';

const getUserFromState = (userInfo) => ({
  username: userInfo?.user_info?.username || '',
  email: userInfo?.user_info?.email || userInfo?.user?.email || '',
});

const removeSupheroWidget = () => {
  document
    .querySelectorAll([
      'script[data-tivigo-suphero-chat="1"]',
      'script[src*="chat_widget.js"]',
      '.tc-root',
    ].join(','))
    .forEach((node) => node.remove());

  delete window.Suphero;
  delete window.SupheroSettings;
};

const clearSupheroStorage = () => {
  Object.keys(localStorage)
    .filter((key) => key.startsWith(SUPHERO_STORAGE_PREFIX))
    .forEach((key) => localStorage.removeItem(key));
};

const supportChatIdentityKey = (visitor) => {
  if (!visitor?.auth?.user_id || !visitor?.auth?.email) return 'guest';
  return `tivigo:${visitor.auth.user_id}:${visitor.auth.email}`;
};

function SupheroChatWidget({ isLoggedIn, userInfo }) {
  const fallbackUser = useMemo(
    () => getUserFromState(userInfo),
    [userInfo]
  );
  const [visitor, setVisitor] = useState(null);

  useEffect(() => {
    if (!isLoggedIn) {
      setVisitor({ username: '', email: '', auth: null });
      return undefined;
    }

    const token = localStorage.getItem('xtream_api_token');
    if (!token) {
      setVisitor({ username: '', email: '', auth: null });
      return undefined;
    }

    const controller = new AbortController();

    const fetchLiveUser = async () => {
      try {
        const response = await fetch(`${USER_DATA_ENDPOINT}&token=${encodeURIComponent(token)}&_=${Date.now()}`, {
          cache: 'no-store',
          signal: controller.signal,
        });
        const result = await response.json();
        const user = result?.data?.user;
        const auth = result?.data?.support_chat_auth;

        if (result?.success && user && auth?.user_id && auth?.email && auth?.signature) {
          setVisitor({
            username: user.username || fallbackUser.username || '',
            email: auth.email || user.email || fallbackUser.email || '',
            auth,
          });
          return;
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
      }

      setVisitor({ username: '', email: '', auth: null });
    };

    fetchLiveUser();
    return () => controller.abort();
  }, [isLoggedIn, fallbackUser]);

  useEffect(() => {
    if (!visitor) return undefined;

    const identityKey = supportChatIdentityKey(visitor);
    const previousIdentityKey = localStorage.getItem(`${SUPHERO_STORAGE_PREFIX}identityKey`) || '';
    removeSupheroWidget();

    if (previousIdentityKey !== identityKey) {
      clearSupheroStorage();
      localStorage.setItem(`${SUPHERO_STORAGE_PREFIX}identityKey`, identityKey);
    }

    const script = document.createElement('script');
    script.async = false;
    script.src = SUPHERO_SCRIPT_SRC;
    script.dataset.tivigoSupheroChat = '1';
    script.dataset.apiBase = SUPHERO_API_BASE;

    if (visitor.username) script.dataset.userName = visitor.username;
    if (visitor.email) script.dataset.userEmail = visitor.email;
    if (visitor.auth?.user_id) script.dataset.tivigoUserId = String(visitor.auth.user_id);
    if (visitor.auth?.email) script.dataset.tivigoAuthEmail = visitor.auth.email;
    if (visitor.auth?.issued_at) script.dataset.tivigoAuthIssuedAt = String(visitor.auth.issued_at);
    if (visitor.auth?.expires_at) script.dataset.tivigoAuthExpiresAt = String(visitor.auth.expires_at);
    if (visitor.auth?.signature) script.dataset.tivigoAuthSignature = visitor.auth.signature;
    script.addEventListener('load', notifySupheroChatReady, { once: true });

    document.body.appendChild(script);
    return removeSupheroWidget;
  }, [visitor]);

  return null;
}

export default SupheroChatWidget;
